import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";

/**
 * The development sign-in shows only in a development build with `REACT_APP_DEV_LOGIN=true`, so a
 * production build never offers it even when that variable is set. The account service accepts
 * these sign-ins only when it runs with DEV_AUTH_SECRET, which it refuses in production
 * (docs/local-review.md).
 */
export const isDevLoginEnabled = (env: {
  NODE_ENV?: string;
  REACT_APP_DEV_LOGIN?: string;
}) => env.NODE_ENV !== "production" && env.REACT_APP_DEV_LOGIN === "true";

export const DEV_LOGIN_ENABLED = isDevLoginEnabled(process.env);

/** The seeded Demo USD accounts (`yarn seed:demo` in account-service). */
export const DEV_ACCOUNTS: readonly {
  email: string;
  labelKey: string;
  label: string;
}[] = [
  {
    email: "platform.admin@pft-platform.example",
    labelKey: "auth.dev.platformAdmin",
    label: "Platform admin",
  },
  {
    email: "admin@demo-usd.example",
    labelKey: "auth.dev.districtAdmin",
    label: "District admin",
  },
  {
    email: "principal@demo-usd.example",
    labelKey: "auth.dev.schoolAdmin",
    label: "School admin",
  },
  {
    email: "teacher1@demo-usd.example",
    labelKey: "auth.dev.teacherLincoln",
    label: "Teacher (Lincoln)",
  },
  {
    email: "teacher3@demo-usd.example",
    labelKey: "auth.dev.teacherRoosevelt",
    label: "Teacher (Roosevelt)",
  },
];

interface DevSignInProps {
  /** The email typed in the form; offered as one more choice when it looks like an address. */
  email: string;
  busy: boolean;
  onSignIn: (email: string) => void;
}

/** One-click sign-in as a seeded account, for local review only. */
export const DevSignIn = ({ email, busy, onSignIn }: DevSignInProps) => {
  const { t } = useTranslation();
  const typed = email.trim();
  return (
    <Box
      component="section"
      aria-labelledby="dev-sign-in-title"
      sx={(theme) => ({
        border: `1px dashed ${theme.palette.divider}`,
        borderRadius: "16px",
        p: 2,
        display: "flex",
        flexDirection: "column",
        gap: 1.25,
      })}
    >
      <Typography
        id="dev-sign-in-title"
        component="h2"
        sx={{ m: 0, fontSize: 14, fontWeight: 800 }}
      >
        {t("auth.dev.title", { defaultValue: "Development sign-in" })}
      </Typography>
      <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>
        {t("auth.dev.body", {
          defaultValue:
            "Local review only. Signs in as a seeded account without a password.",
        })}
      </Typography>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
        {DEV_ACCOUNTS.map((account) => (
          <Button
            key={account.email}
            type="button"
            variant="outlined"
            size="small"
            disabled={busy}
            onClick={() => onSignIn(account.email)}
            title={account.email}
            sx={{ minHeight: 40 }}
          >
            {t(account.labelKey, { defaultValue: account.label })}
          </Button>
        ))}
        {typed.includes("@") ? (
          <Button
            type="button"
            variant="text"
            size="small"
            disabled={busy}
            onClick={() => onSignIn(typed)}
            sx={{ minHeight: 40 }}
          >
            {t("auth.dev.asTyped", {
              defaultValue: "Sign in as {{email}}",
              email: typed,
            })}
          </Button>
        ) : null}
      </Box>
    </Box>
  );
};

export default DevSignIn;
